import { useEffect, useMemo, useState } from "react";
import { useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import {
  Image,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import {
  BookOpen,
  Calendar,
  Clock,
  FileText,
  Link,
  MapPin,
  Timer,
  Video,
} from "lucide-react-native";
import { EMBER } from "../../../../onboarding/theme";
import {
  calculateAppointmentDurationMinutes,
  useDeleteAppointment,
  usePatientNotes,
  useUpdatePatientNote,
} from "@src/packages/care";
import { useDocuments } from "@src/packages/documents";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  formatDate,
  formatTimeHHMM,
  useLocale,
  useTranslations,
} from "@src/packages/i18n";
import { localizeAppointmentTitle } from "../localizeAppointmentTitle";
import { getProcedureByName } from "@src/packages/content";
import {
  Dialog,
  DialogIcon,
  DialogPrimaryButton,
  MoonDetailRow,
  SoftSecondaryButton,
  Tag,
  TextActionButton,
} from "@louise/moon-ui";
import { DocumentRow } from "../../documents/DocumentRow";
import { useDocumentUploadController } from "../../../DocumentUploadController";
import type { CareStackParamList } from "../../../types";
import type { AppointmentDetailItem } from "../schedule.types";
import { canManageAppointment } from "@src/packages/care/appointment-ownership";
import {
  getTodayActionCompletionRecord,
  parseTodayActionCompletion,
  serializeTodayActionCompletion,
  type TodayActionCompletion,
} from "@src/packages/care/todayActionCompletion";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const trashIllustration =
  require("../../../../assets/images/you-icons/trash-bin.png") as number;
const appointmentIllustration =
  require("../../../../assets/images/treatment-schedule.png") as number;

type Props = {
  visible: boolean;
  onClose: () => void;
  appointment: AppointmentDetailItem | null;
  onEdit?: (appointment: AppointmentDetailItem) => void;
  onOpenEducation?: (
    appointment: AppointmentDetailItem,
    activityName?: string,
  ) => void;
};

const APPOINTMENT_DOCUMENT_TAG_PREFIX = "today-action:";

function getAppointmentDocumentTag(appointmentId: string) {
  return `${APPOINTMENT_DOCUMENT_TAG_PREFIX}${appointmentId}`;
}

function isDevEvidenceRouteEnabled() {
  const params = getDevEvidenceSearchParams();
  if (!params || typeof window === "undefined") return false;
  if (__DEV__ || process.env.EXPO_PUBLIC_DEV_TOOLS === "true") return true;
  return (
    params.get("test") === "1" &&
    (params.has("dev_care_appointment_detail") ||
      params.has("dev_appointment_completion"))
  );
}

function getDevEvidenceSearchParams() {
  if (typeof window === "undefined") return null;
  const current = new URLSearchParams(window.location.search);
  try {
    const stored = window.sessionStorage?.getItem("__pr_visual_route_search");
    if (!stored) return current;
    for (const [key, value] of new URLSearchParams(stored)) {
      if (!current.has(key)) current.set(key, value);
    }
    return current;
  } catch {
    return current;
  }
}

function getDevAppointmentDetailMode(): "empty" | "note" | null {
  try {
    const params = getDevEvidenceSearchParams();
    if (!params) return null;
    const mode = params.get("dev_care_appointment_detail");
    if (!isDevEvidenceRouteEnabled() && params.get("test") !== "1") {
      return null;
    }
    return mode === "empty" || mode === "note" ? mode : null;
  } catch {
    return null;
  }
}

function getDevAppointmentCompletion(
  actionId: string,
): TodayActionCompletion | null {
  try {
    const params = getDevEvidenceSearchParams();
    if (!params) return null;
    if (!isDevEvidenceRouteEnabled() && params.get("test") !== "1") {
      return null;
    }
    const raw = params.get("dev_appointment_completion");
    const completion = parseTodayActionCompletion(raw);
    return completion?.actionId === actionId ? completion : null;
  } catch {
    return null;
  }
}

export function AppointmentDetailSheet({
  visible,
  onClose,
  appointment,
  onEdit,
  onOpenEducation,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("AppointmentDetailSheet");
  const { locale } = useLocale();
  const navigation = useNavigation<NavigationProp<CareStackParamList>>();
  const { patientId } = useApiClient();
  const {
    data: patientNotes,
    isLoading: isLoadingPatientNotes,
    isError: isPatientNotesError,
  } = usePatientNotes();
  const { height: windowHeight } = useWindowDimensions();
  const deleteAppointment = useDeleteAppointment();
  const updatePatientNote = useUpdatePatientNote();
  const { data: documents } = useDocuments();
  const { pickAndOpenDocumentUpload } = useDocumentUploadController();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isEditingPatientCompletion, setIsEditingPatientCompletion] =
    useState(false);
  const [patientCompletionDraft, setPatientCompletionDraft] = useState("");
  const [patientCompletionError, setPatientCompletionError] = useState<
    string | null
  >(null);

  useEffect(() => {
    setIsDeleting(false);
    setConfirmingDelete(false);
    setDeleteError(null);
    setIsEditingPatientCompletion(false);
    setPatientCompletionDraft("");
    setPatientCompletionError(null);
  }, [appointment?.appointmentId, visible]);

  const completionActionId =
    appointment?.appointmentId ?? appointment?.id ?? "";
  const devAppointmentDetailMode = getDevAppointmentDetailMode();
  const devAppointmentCompletion = useMemo(
    () => getDevAppointmentCompletion(completionActionId),
    [completionActionId],
  );
  const patientCompletionRecord = useMemo(
    () => getTodayActionCompletionRecord(patientNotes, completionActionId),
    [patientNotes, completionActionId],
  );
  const patientCompletion = useMemo(
    () =>
      devAppointmentCompletion ??
      (devAppointmentDetailMode === "empty"
        ? null
        : patientCompletionRecord?.completion),
    [
      devAppointmentCompletion,
      devAppointmentDetailMode,
      patientCompletionRecord,
    ],
  );
  const patientCompletionNoteId = devAppointmentCompletion
    ? null
    : (patientCompletionRecord?.note.id ?? null);
  const appointmentDocumentTag = appointment
    ? getAppointmentDocumentTag(appointment.appointmentId ?? appointment.id)
    : null;
  const appointmentDocuments = useMemo(
    () =>
      (documents ?? [])
        .filter(
          (document) =>
            appointmentDocumentTag != null &&
            document.tags.includes(appointmentDocumentTag),
        )
        .sort((a, b) => {
          const dateA = a.documentDate ?? a.createdAt ?? "";
          const dateB = b.documentDate ?? b.createdAt ?? "";
          return dateB.localeCompare(dateA);
        }),
    [appointmentDocumentTag, documents],
  );

  if (!appointment) return null;

  const canManage = canManageAppointment(appointment, patientId);
  const canEdit = canManage && Boolean(onEdit);
  const activities = Array.from(
    new Map(
      (appointment.consultationType ?? [])
        .map((activity) => activity.trim())
        .filter(Boolean)
        .map((activity) => [activity.toLowerCase(), activity] as const),
    ).values(),
  );
  const activityFactsheets = new Map(
    activities.map(
      (activity) => [activity, getProcedureByName(activity)] as const,
    ),
  );
  const hasInteractiveActivity =
    Boolean(onOpenEducation) &&
    Array.from(activityFactsheets.values()).some(Boolean);
  const canLearn = Boolean(
    onOpenEducation && appointment.factsheetId && !hasInteractiveActivity,
  );
  const appointmentTitle = localizeAppointmentTitle(appointment.title, t);
  const dateLabel = formatDate(appointment.date, locale, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timeLabel = formatTimeHHMM(appointment.time, locale);
  const durationMinutes =
    appointment.time && appointment.endDate && appointment.endTime
      ? calculateAppointmentDurationMinutes({
          startDate: appointment.date,
          startTime: appointment.time,
          endDate: appointment.endDate,
          endTime: appointment.endTime,
          timeZone: appointment.timeZone,
        })
      : null;
  const durationLabel = durationMinutes
    ? durationMinutes >= 60 && durationMinutes % 60 === 0
      ? t("add_appointment.duration_hours_value", {
          params: { count: String(durationMinutes / 60) },
        })
      : t("add_appointment.duration_minutes_value", {
          params: { count: String(durationMinutes) },
        })
    : "";
  const hasPhysicalLocation = Boolean(appointment.location?.trim());
  const shouldShowFormat =
    appointment.isOnline === true ||
    (appointment.isOnline === false && !hasPhysicalLocation);
  const endTimeLabel = formatTimeHHMM(appointment.endTime, locale);
  const endLabel = appointment.endDate
    ? appointment.endDate === appointment.date
      ? endTimeLabel
      : t("add_appointment.end_value_next_day", {
          params: {
            date: formatDate(appointment.endDate, locale, {
              weekday: "short",
              month: "short",
              day: "numeric",
            }),
            time: endTimeLabel,
          },
        })
    : "";

  const handleClose = () => {
    setIsDeleting(false);
    setConfirmingDelete(false);
    setDeleteError(null);
    onClose();
  };

  const handleEdit = () => {
    if (onEdit && appointment) {
      onEdit(appointment);
      handleClose();
    }
  };

  const doDelete = () => {
    if (!canManage || !appointment.appointmentId) return;
    setDeleteError(null);
    setIsDeleting(true);
    deleteAppointment.mutate(appointment.appointmentId, {
      onSuccess: () => {
        handleClose();
      },
      onError: () => {
        setIsDeleting(false);
        setDeleteError(t("appointment_detail.delete_error"));
      },
    });
  };

  const handleDelete = () => {
    setDeleteError(null);
    setConfirmingDelete(true);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setConfirmingDelete(false);
    setDeleteError(null);
  };

  const handleStartEditingPatientCompletion = () => {
    if (!patientCompletionNoteId || !patientCompletion) return;
    setPatientCompletionDraft(patientCompletion.notes);
    setPatientCompletionError(null);
    setIsEditingPatientCompletion(true);
  };

  const handleCancelEditingPatientCompletion = () => {
    if (updatePatientNote.isPending) return;
    setPatientCompletionError(null);
    setIsEditingPatientCompletion(false);
  };

  const handleSavePatientCompletion = async () => {
    if (!patientCompletionNoteId || !patientCompletion) return;

    setPatientCompletionError(null);
    try {
      await updatePatientNote.mutateAsync({
        noteId: patientCompletionNoteId,
        text: serializeTodayActionCompletion({
          actionId: patientCompletion.actionId,
          kind: patientCompletion.kind,
          status: patientCompletion.status,
          date: patientCompletion.date,
          notes: patientCompletionDraft.trim(),
          recordedAt: patientCompletion.recordedAt,
          source: patientCompletion.source,
        }),
      });
      setIsEditingPatientCompletion(false);
    } catch {
      setPatientCompletionError(t("appointment_detail.completion_save_error"));
    }
  };

  const handleUploadAppointmentDocument = () => {
    if (!appointmentDocumentTag) return;
    void pickAndOpenDocumentUpload({
      defaultCategory: "medical_records",
      defaultTags: [appointmentDocumentTag],
    });
  };

  const handleOpenAppointmentDocument = (documentId: string) => {
    onClose();
    navigation.navigate("Documents", { initialDocumentId: documentId });
  };

  const handleLearnMore = () => {
    if (!onOpenEducation || !appointment.factsheetId) return;
    onOpenEducation(appointment);
    handleClose();
  };

  const handleLearnAboutActivity = (activityName: string) => {
    if (!onOpenEducation || !activityFactsheets.get(activityName)) return;
    onOpenEducation(appointment, activityName);
    handleClose();
  };

  const dialogMaxHeight = windowHeight * 0.82;

  return (
    <>
      <Dialog
        visible={visible}
        onClose={handleClose}
        animationType={getDevAppointmentDetailMode() ? "none" : undefined}
        layout="centered"
        closeButton="minimal"
        containerStyle={{
          width: "90%",
          maxWidth: 400,
          maxHeight: dialogMaxHeight,
        }}
        surfaceStyle={{ maxHeight: dialogMaxHeight }}
        contentStyle={{
          maxHeight: dialogMaxHeight,
          paddingTop: 30,
          paddingHorizontal: 24,
          paddingBottom: 24,
        }}
        testID="appointment-detail-dialog"
      >
        <View
          testID="appointment-detail-hero"
          style={{
            alignItems: "center",
            paddingHorizontal: 24,
            marginBottom: 20,
          }}
        >
          <DialogIcon
            source={appointmentIllustration}
            width={72}
            height={72}
            style={{ marginBottom: 12 }}
            testID="appointment-detail-illustration"
          />
          <Text
            accessibilityRole="header"
            className="font-dm-sans"
            style={{
              fontSize: 22,
              fontWeight: "700",
              color: EMBER.colors.text,
              marginBottom: 4,
              textAlign: "center",
            }}
          >
            {appointmentTitle}
          </Text>
          {activities.length ? (
            <View
              testID="appointment-activity-tags"
              className="flex-row flex-wrap"
              style={{ gap: 7, marginTop: 10, justifyContent: "center" }}
            >
              {activities.map((activity) => {
                const label = localizeAppointmentTitle(activity, t);
                const isInteractive = Boolean(
                  onOpenEducation && activityFactsheets.get(activity),
                );
                const tag = (
                  <Tag
                    label={label}
                    variant="outline"
                    icon={isInteractive ? BookOpen : undefined}
                  />
                );

                return isInteractive ? (
                  <Pressable
                    key={activity}
                    onPress={() => {
                      capturePress(
                        pressLabel(label),
                        "appointment_activity_tag",
                      );
                      handleLearnAboutActivity(activity);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel={t(
                      "appointment_detail.learn_about_activity",
                      { params: { activity: label } },
                    )}
                  >
                    {tag}
                  </Pressable>
                ) : (
                  <View key={activity}>{tag}</View>
                );
              })}
            </View>
          ) : null}
        </View>

        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ flexShrink: 1, maxHeight: windowHeight * 0.48 }}
          contentContainerStyle={{ gap: 14, paddingBottom: 4 }}
        >
          {patientCompletion?.notes ? (
            <MoonDetailRow
              testID="appointment-patient-completion"
              icon={FileText}
              label={t("appointment_detail.completion_title")}
              value={
                isEditingPatientCompletion ? (
                  <PatientCompletionEditor
                    draft={patientCompletionDraft}
                    error={patientCompletionError}
                    isSaving={updatePatientNote.isPending}
                    t={t}
                    onCancel={handleCancelEditingPatientCompletion}
                    onChange={setPatientCompletionDraft}
                    onSave={handleSavePatientCompletion}
                  />
                ) : (
                  patientCompletion.notes
                )
              }
              trailing={
                patientCompletionNoteId && !isEditingPatientCompletion ? (
                  <TextActionButton
                    label={t("appointment_detail.completion_edit")}
                    onPress={handleStartEditingPatientCompletion}
                    color={EMBER.colors.coral}
                    style={{ paddingHorizontal: 0, paddingVertical: 0 }}
                  />
                ) : null
              }
            />
          ) : isLoadingPatientNotes &&
            completionActionId &&
            devAppointmentDetailMode !== "empty" ? (
            <PatientCompletionState
              testID="appointment-patient-completion-loading"
              message={t("appointment_detail.completion_loading")}
            />
          ) : isPatientNotesError &&
            completionActionId &&
            devAppointmentDetailMode !== "empty" ? (
            <PatientCompletionState
              testID="appointment-patient-completion-unavailable"
              message={t("appointment_detail.completion_unavailable")}
            />
          ) : null}

          {appointment.date ? (
            <MoonDetailRow
              icon={Calendar}
              label={t("appointment_detail.row_date")}
              value={dateLabel}
            />
          ) : null}

          {appointment.time ? (
            <MoonDetailRow
              icon={Clock}
              label={t("appointment_detail.row_time")}
              value={timeLabel}
            />
          ) : null}

          {durationMinutes ? (
            <MoonDetailRow
              icon={Timer}
              label={t("appointment_detail.row_duration")}
              value={durationLabel}
            />
          ) : null}

          {!durationMinutes && appointment.endDate && appointment.endTime ? (
            <MoonDetailRow
              icon={Clock}
              label={t("appointment_detail.row_ends")}
              value={endLabel}
            />
          ) : null}

          {shouldShowFormat ? (
            <MoonDetailRow
              icon={Video}
              label={t("appointment_detail.row_format")}
              value={
                appointment.isOnline
                  ? t("add_appointment.format_online")
                  : t("add_appointment.format_in_person")
              }
            />
          ) : null}

          {/* Format owns the destination. For legacy records without a format,
            retain whichever destination data is available. */}
          {appointment.isOnline !== true && hasPhysicalLocation ? (
            <MoonDetailRow
              icon={MapPin}
              label={t("appointment_detail.row_location")}
              value={appointment.location}
            />
          ) : null}

          {appointment.isOnline !== false && appointment.meetingLink ? (
            <MoonDetailRow
              icon={Link}
              label={t("add_appointment.summary_meeting_link")}
              value={appointment.meetingLink}
            />
          ) : null}

          {appointment.notes ? (
            <MoonDetailRow
              icon={FileText}
              label={t("appointment_detail.appointment_notes")}
              value={appointment.notes}
            />
          ) : null}

          {appointmentDocumentTag && appointmentDocuments.length > 0 ? (
            <MoonDetailRow
              testID="appointment-documents"
              icon={FileText}
              label={t("appointment_detail.documents_title")}
              value={
                <View style={{ gap: 8 }}>
                  {appointmentDocuments.map((document) => (
                    <DocumentRow
                      key={document.id}
                      title={document.name}
                      dateLabel={getAppointmentDocumentDateLabel(
                        document.documentDate ?? document.createdAt,
                        locale,
                      )}
                      icon={
                        <FileText
                          size={17}
                          color={EMBER.colors.warm}
                          strokeWidth={1.6}
                        />
                      }
                      onPress={() => handleOpenAppointmentDocument(document.id)}
                      showChevron
                    />
                  ))}
                </View>
              }
            />
          ) : null}
        </ScrollView>

        <View style={{ paddingTop: 20, gap: 10 }}>
          {canManage ? (
            <>
              {appointmentDocumentTag ? (
                <DialogPrimaryButton
                  label={t("appointment_detail.documents_upload")}
                  onPress={handleUploadAppointmentDocument}
                />
              ) : canLearn ? (
                <DialogPrimaryButton
                  label={t("appointment_detail.learn_more")}
                  onPress={handleLearnMore}
                />
              ) : null}
              {canLearn && appointmentDocumentTag ? (
                <SoftSecondaryButton
                  label={t("appointment_detail.learn_more")}
                  onPress={handleLearnMore}
                />
              ) : null}
              {canEdit ? (
                <SoftSecondaryButton
                  label={t("appointment_detail.edit")}
                  onPress={handleEdit}
                />
              ) : null}
              <TextActionButton
                label={t("appointment_detail.delete")}
                onPress={handleDelete}
                color={EMBER.colors.coral}
                style={{ alignSelf: "center" }}
              />
            </>
          ) : canLearn ? (
            <>
              {appointmentDocumentTag ? (
                <DialogPrimaryButton
                  label={t("appointment_detail.documents_upload")}
                  onPress={handleUploadAppointmentDocument}
                />
              ) : null}
              <SoftSecondaryButton
                label={t("appointment_detail.learn_more")}
                onPress={handleLearnMore}
              />
            </>
          ) : appointmentDocumentTag ? (
            <DialogPrimaryButton
              label={t("appointment_detail.documents_upload")}
              onPress={handleUploadAppointmentDocument}
            />
          ) : null}
        </View>
      </Dialog>

      <Dialog
        visible={visible && confirmingDelete}
        onClose={handleCancelDelete}
        layout="centered"
        closeButton="minimal"
        dismissOnBackdropTap={false}
        testID="appointment-delete-confirmation-dialog"
      >
        <Image
          testID="appointment-delete-trash-illustration"
          source={trashIllustration}
          style={{ width: 80, height: 96, alignSelf: "center" }}
          resizeMode="contain"
        />
        <Text
          accessibilityRole="header"
          className="font-dm-sans"
          style={{
            color: EMBER.colors.text,
            fontSize: 22,
            fontWeight: "700",
            lineHeight: 28,
            marginTop: 16,
            marginBottom: 10,
            textAlign: "center",
          }}
        >
          {t("appointment_detail.delete_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textMid,
            fontSize: 14,
            lineHeight: 20,
            marginBottom: 20,
            textAlign: "center",
          }}
        >
          {t("appointment_detail.delete_confirm", {
            params: { title: appointmentTitle },
          })}
        </Text>
        {deleteError ? (
          <Text
            accessibilityLiveRegion="polite"
            className="font-dm-sans"
            style={{
              borderRadius: 10,
              backgroundColor: "rgba(168,86,58,0.08)",
              color: EMBER.colors.coral,
              fontSize: 13,
              lineHeight: 18,
              marginBottom: 12,
              paddingHorizontal: 12,
              paddingVertical: 10,
              textAlign: "center",
            }}
          >
            {deleteError}
          </Text>
        ) : null}
        <View testID="appointment-delete-actions" style={{ gap: 10 }}>
          <DialogPrimaryButton
            label={
              deleteError
                ? t("appointment_detail.delete_retry")
                : t("appointment_detail.confirm_delete")
            }
            onPress={doDelete}
            loading={isDeleting}
          />
          <SoftSecondaryButton
            label={t("appointment_detail.cancel")}
            onPress={handleCancelDelete}
          />
        </View>
      </Dialog>
    </>
  );
}

function PatientCompletionEditor({
  draft,
  error,
  isSaving,
  t,
  onCancel,
  onChange,
  onSave,
}: {
  draft: string;
  error: string | null;
  isSaving: boolean;
  t: ReturnType<typeof useTranslations>;
  onCancel: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
}) {
  return (
    <View style={{ gap: 9, minWidth: 0 }}>
      <TextInput
        testID="appointment-patient-completion-input"
        className="font-dm-sans"
        accessibilityLabel={t("appointment_detail.completion_input_label")}
        editable={!isSaving}
        multiline
        value={draft}
        onChangeText={onChange}
        style={{
          minHeight: 84,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: "rgba(121,70,51,0.18)",
          backgroundColor: "rgba(121,70,51,0.035)",
          paddingHorizontal: 12,
          paddingVertical: 10,
          color: EMBER.colors.text,
          fontSize: 14,
          lineHeight: 20,
          textAlignVertical: "top",
        }}
      />
      {error ? (
        <Text
          className="font-dm-sans"
          style={{ color: EMBER.colors.coral, fontSize: 12, lineHeight: 17 }}
        >
          {error}
        </Text>
      ) : null}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
        }}
      >
        <TextActionButton
          label={t("appointment_detail.completion_cancel")}
          onPress={onCancel}
          disabled={isSaving}
          style={{ paddingHorizontal: 6, paddingVertical: 4 }}
        />
        <TextActionButton
          label={t("appointment_detail.completion_save")}
          onPress={onSave}
          disabled={isSaving}
          color={EMBER.colors.coral}
          style={{ paddingHorizontal: 6, paddingVertical: 4 }}
        />
      </View>
    </View>
  );
}

function getAppointmentDocumentDateLabel(
  date: string | null,
  locale: Parameters<typeof formatDate>[1],
) {
  return date
    ? formatDate(date, locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : undefined;
}

function PatientCompletionState({
  message,
  testID,
}: {
  message: string;
  testID: string;
}) {
  return (
    <View
      testID={testID}
      accessibilityLiveRegion="polite"
      style={{
        borderRadius: 14,
        backgroundColor: "rgba(121,70,51,0.06)",
        paddingHorizontal: 14,
        paddingVertical: 12,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.textMid,
          fontSize: 12.5,
          lineHeight: 18,
          textAlign: "center",
        }}
      >
        {message}
      </Text>
    </View>
  );
}
